import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, Hash, Heart, Brain, Twitter, Plus as PlusIcon, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import TickerSearchInput from '@/components/TickerSearchInput';
import { shareEntryToX } from '@/utils/journalShare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable journal-entry editor. Used by:
//   • /journal page (new + edit)
//   • Stock Modal Journal tab (new, prefilled with ticker)
//   • Auto-suggest toasts on Add Position / Sell (prefilled with type
//     + ticker + transaction context)
//
// Props:
//   entry           — existing entry to edit (null = new)
//   prefill         — { entry_type, ticker, title, body, transaction_id, context_snapshot }
//   onClose, onSaved

const ENTRY_TYPES = [
  { value: 'thesis_add',   label: 'Thesis on Add' },
  { value: 'thesis_sell',  label: 'Thesis on Sell' },
  { value: 'dividend_cut', label: 'Dividend-Cut Note' },
  { value: 'ex_div',       label: 'Ex-Div Week' },
  { value: 'free',         label: 'Free Note' },
  { value: 'review',       label: 'Periodic Review' },
];

const EMOTIONS = [
  { value: '',           label: '— none —' },
  { value: 'disciplined', label: '🧘 Disciplined' },
  { value: 'patient',    label: '🪷 Patient' },
  { value: 'confident',  label: '😎 Confident' },
  { value: 'curious',    label: '🤔 Curious' },
  { value: 'fomo',       label: '😬 FOMO' },
  { value: 'panic',      label: '😨 Panic' },
  { value: 'regret',     label: '😞 Regret' },
];

export default function JournalEntryModal({ entry, prefill, onClose, onSaved }) {
  const isEdit = !!entry;
  const seed = entry || prefill || {};
  const [title, setTitle] = useState(seed.title || '');
  const [body, setBody] = useState(seed.body || '');
  const [entryType, setEntryType] = useState(seed.entry_type || 'free');
  const [ticker, setTicker] = useState(seed.ticker || '');
  const [emotion, setEmotion] = useState(seed.emotion || '');
  const [conviction, setConviction] = useState(seed.conviction ?? null);
  const [tagsInput, setTagsInput] = useState((seed.tags || []).map(t => `#${t}`).join(' '));
  const [saving, setSaving] = useState(false);
  // Round 71n (Feb 2026): curated tag dropdown. We surface a list of
  // common dividend-investor tags + every tag the user has used
  // before. Click toggles the tag on/off in tagsInput.
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [knownTags, setKnownTags] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/journal/tags`, { withCredentials: true })
      .then((res) => { if (!cancelled) setKnownTags(res.data?.tags || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!body.trim()) {
      toast.error('Entry body cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const tags = tagsInput
        .split(/[\s,]+/)
        .map(t => t.replace(/^#/, '').trim().toLowerCase())
        .filter(Boolean);
      const payload = {
        title: title.trim() || null,
        body: body.trim(),
        entry_type: entryType,
        ticker: (ticker || '').toUpperCase().trim() || null,
        emotion: emotion || null,
        conviction: conviction == null ? null : Number(conviction),
        tags,
        // Preserve transaction_id + context_snapshot from prefill so
        // an entry born from "you just bought SCHD" stays anchored to
        // that exact buy in case the user later edits the body.
        transaction_id: seed.transaction_id || null,
        context_snapshot: seed.context_snapshot || null,
      };
      if (isEdit) {
        await axios.put(`${API}/journal/entries/${entry.entry_id}`, payload, { withCredentials: true });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/journal/entries`, payload, { withCredentials: true });
        toast.success('Entry saved 📓');
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error('Save failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  // Round 71n: helpers for the curated tag picker.
  const _currentTags = () =>
    new Set(
      tagsInput
        .split(/[\s,]+/)
        .map((t) => t.replace(/^#/, '').trim().toLowerCase())
        .filter(Boolean),
    );

  const toggleTag = (tag) => {
    const t = (tag || '').toLowerCase().trim();
    if (!t) return;
    const set = _currentTags();
    if (set.has(t)) set.delete(t); else set.add(t);
    setTagsInput([...set].map((x) => `#${x}`).join(' '));
  };

  // Curated dividend-investor tags every user can pick from. Combined
  // with the user's own historical tags so the picker grows with use.
  const SUGGESTED_TAGS = [
    'core-position', 'high-conviction', 'speculative',
    'dividend-aristocrat', 'dividend-king', 'reit', 'bdc', 'mlp',
    'cef', 'covered-call-etf', 'monthly-payer', 'k-1-issuer',
    'yield-trap', 'thesis-on-add', 'thesis-on-trim',
    'dividend-cut', 'dividend-raise', 'macro', 'rate-cycle',
  ];

  const allPickerTags = (() => {
    const seen = new Set();
    const merged = [];
    [...knownTags.map((k) => (k.tag || k.name || k).toLowerCase()), ...SUGGESTED_TAGS]
      .forEach((t) => {
        if (!seen.has(t)) { seen.add(t); merged.push(t); }
      });
    return merged;
  })();

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" data-testid="journal-entry-modal">
      <div className="bg-background rounded-xl border-2 border-violet-400/60 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-4 pb-24 sm:pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-violet-700 dark:text-violet-300">
            {isEdit ? 'Edit entry' : 'New journal entry'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" data-testid="journal-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Type</label>
            <select
              value={entryType}
              onChange={(e) => setEntryType(e.target.value)}
              disabled={isEdit}
              data-testid="journal-modal-type-select"
              className="mt-1 w-full h-10 px-2 rounded-md border bg-background text-sm disabled:opacity-60"
            >
              {ENTRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              {isEdit && entry?.entry_type === 'jnl_starter' && (
                <option value="jnl_starter">JNL Starter (locked)</option>
              )}
            </select>
            {isEdit && (
              <p className="text-[10px] text-muted-foreground mt-1">Type is locked once saved — keeps the audit trail honest.</p>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Ticker (optional)</label>
            {isEdit ? (
              <Input
                value={ticker}
                disabled
                data-testid="journal-modal-ticker-input"
                className="mt-1 disabled:opacity-60"
              />
            ) : (
              <TickerSearchInput
                value={ticker}
                onChange={setTicker}
                placeholder="Search ticker, e.g. SCHD"
                testId="journal-modal-ticker-input"
                className="mt-1 font-mono text-sm h-10"
              />
            )}
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Title (optional)</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Adding to core SCHD position"
            data-testid="journal-modal-title-input"
            className="mt-1"
          />
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Body</label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What's your thinking? Thesis, why now, what would change your mind, target hold period…"
            rows={8}
            data-testid="journal-modal-body-input"
            className="mt-1 font-mono text-sm"
          />
          <p className="text-[10px] text-muted-foreground mt-1">{body.length} chars · plain text or markdown</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground inline-flex items-center gap-1">
              <Heart className="w-3 h-3" /> Emotion
            </label>
            <select
              value={emotion}
              onChange={(e) => setEmotion(e.target.value)}
              data-testid="journal-modal-emotion-select"
              className="mt-1 w-full h-10 px-2 rounded-md border bg-background text-sm"
            >
              {EMOTIONS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground inline-flex items-center gap-1">
              <Brain className="w-3 h-3" /> Conviction (1–10)
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="range"
                min="1"
                max="10"
                value={conviction ?? 5}
                onChange={(e) => setConviction(Number(e.target.value))}
                data-testid="journal-modal-conviction-slider"
                className="flex-1"
              />
              <span className="w-12 text-center text-sm font-mono">
                {conviction == null ? '—' : `${conviction}/10`}
              </span>
              {conviction != null && (
                <button
                  type="button"
                  onClick={() => setConviction(null)}
                  className="text-[10px] text-muted-foreground hover:text-foreground"
                  data-testid="journal-modal-conviction-clear"
                >
                  clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground inline-flex items-center gap-1">
              <Hash className="w-3 h-3" /> Tags
            </label>
            <button
              type="button"
              onClick={() => setShowTagPicker((v) => !v)}
              className="text-[10px] text-violet-600 dark:text-violet-400 hover:underline inline-flex items-center gap-0.5"
              data-testid="journal-modal-tag-picker-toggle"
            >
              <PlusIcon className="w-3 h-3" />
              {showTagPicker ? 'Hide picker' : 'Pick from list'}
            </button>
          </div>
          <Input
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="#dividend-aristocrat #core-position"
            data-testid="journal-modal-tags-input"
          />
          {showTagPicker && (
            <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-border bg-muted/30 p-2 grid grid-cols-2 gap-1" data-testid="journal-modal-tag-picker">
              {allPickerTags.map((t) => {
                const checked = _currentTags().has(t);
                return (
                  <label
                    key={t}
                    className={`flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer text-xs hover:bg-accent ${checked ? 'bg-violet-100 dark:bg-violet-950/40' : ''}`}
                    data-testid={`journal-modal-tag-chip-${t}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleTag(t)}
                      className="accent-violet-600"
                    />
                    <span className={checked ? 'font-semibold text-violet-700 dark:text-violet-300' : ''}>#{t}</span>
                  </label>
                );
              })}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">
            Common tags are pre-listed. Add your own anytime — they\'ll appear next time.
          </p>
        </div>

        <div className="flex gap-2 justify-end pt-2 border-t mt-1">
          {isEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => shareEntryToX(entry)}
              className="mr-auto text-sky-600 border-sky-300 dark:border-sky-800 hover:bg-sky-50 dark:hover:bg-sky-950/30"
              data-testid="journal-modal-share-x"
              title="Open X with this entry pre-filled (text + your tags as hashtags)"
            >
              <Twitter className="w-4 h-4 mr-1" /> Share to X
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="journal-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !body.trim()}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="journal-modal-save"
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save entry'}
          </Button>
        </div>
      </div>
    </div>
  );
}
